import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Plus, MoreHorizontal, Pencil, Copy, Trash2, Loader2, Layout,
  FileText, Eye, Save, ArrowLeft, GripVertical, ChevronUp, ChevronDown, X,
  Search, SlidersHorizontal, ArrowUpDown, Grid3X3, List, Filter, Layers,
  Building2, Calendar, Sparkles, Tag, LayoutGrid, Home, User, Mail, File, Megaphone, ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { landingPagesApi } from '@/api/landingPages';
import { IconPicker, DynamicIcon } from '@/components/IconPicker';
import { useAuth } from '@/contexts/AuthContext';

const CATEGORIES = [
  { value: 'all', label: 'All Categories', icon: LayoutGrid },
  { value: 'real_estate', label: 'Real Estate', icon: Building2 },
  { value: 'business', label: 'Business', icon: Layers },
  { value: 'events', label: 'Events', icon: Calendar },
  { value: 'marketing', label: 'Marketing', icon: Sparkles },
  { value: 'custom', label: 'Custom', icon: Tag },
];

const SORT_OPTIONS = [
  { value: 'name_asc', label: 'Name (A-Z)' },
  { value: 'name_desc', label: 'Name (Z-A)' },
  { value: 'sections_desc', label: 'Most Sections' },
  { value: 'sections_asc', label: 'Fewest Sections' },
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
];

const VIEW_MODES = [
  { value: 'grid', icon: Grid3X3 },
  { value: 'list', icon: List },
];

const SECTION_TYPES = [
  { type: 'hero', label: 'Hero Banner', description: 'Full-width header with image/video' },
  { type: 'logo_overlay', label: 'Logo Overlay', description: 'Centered logo over hero' },
  { type: 'text', label: 'Text Block', description: 'Title and body text' },
  { type: 'image_gallery', label: 'Image Gallery', description: 'Photo grid' },
  { type: 'property_details', label: 'Property Details', description: 'Price, beds, baths' },
  { type: 'features', label: 'Features List', description: 'Icon and text features' },
  { type: 'agent_card', label: 'Agent Card', description: 'Agent profile' },
  { type: 'map', label: 'Location Map', description: 'Interactive map' },
  { type: 'video', label: 'Video Embed', description: 'YouTube/Vimeo video' },
  { type: 'contact_form', label: 'Contact Form', description: 'Lead capture form' },
  { type: 'pdf_download', label: 'PDF Download', description: 'Download button' },
  { type: 'document_list', label: 'Document List', description: 'Grid of documents' },
];

// Icon map for template types
const TEMPLATE_ICONS = {
  home: Home,
  user: User,
  calendar: Calendar,
  megaphone: Megaphone,
  mail: Mail,
  file: File,
  layout: Layout,
  building: Building2,
  sparkles: Sparkles,
  layers: Layers,
};

// Get icon component for template
const getTemplateIcon = (iconName) => {
  const IconComponent = TEMPLATE_ICONS[iconName] || Layout;
  return <IconComponent className="w-full h-full" />;
};

export default function PageTemplates() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [sortBy, setSortBy] = useState('name_asc');
  const [viewMode, setViewMode] = useState('grid');
  const [showSystemTemplates, setShowSystemTemplates] = useState(true);
  const [showCustomTemplates, setShowCustomTemplates] = useState(true);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'system', 'custom'
  
  // Dialog state
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteTemplate, setDeleteTemplate] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state for create/edit
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'custom',
    icon: null,
    sections: []
  });
  
  // Filtered and sorted templates
  const filteredTemplates = useMemo(() => {
    let result = [...templates];
    
    // Filter by tab (system vs custom)
    if (activeTab === 'system') {
      result = result.filter(t => t.is_system);
    } else if (activeTab === 'custom') {
      result = result.filter(t => !t.is_system);
    }
    
    // Filter by category
    if (category !== 'all') {
      result = result.filter(t => t.category === category);
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(t => 
        t.name?.toLowerCase().includes(query) ||
        t.description?.toLowerCase().includes(query) ||
        t.category?.toLowerCase().includes(query) ||
        t.sections?.some(s => s.type?.toLowerCase().includes(query))
      );
    }
    
    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'name_asc':
          return (a.name || '').localeCompare(b.name || '');
        case 'name_desc':
          return (b.name || '').localeCompare(a.name || '');
        case 'sections_desc':
          return (b.sections?.length || 0) - (a.sections?.length || 0);
        case 'sections_asc':
          return (a.sections?.length || 0) - (b.sections?.length || 0);
        case 'newest':
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        case 'oldest':
          return new Date(a.created_at || 0) - new Date(b.created_at || 0);
        default:
          return 0;
      }
    });
    
    return result;
  }, [templates, searchQuery, category, sortBy, activeTab]);
  
  // Stats for display
  const stats = useMemo(() => {
    const systemCount = templates.filter(t => t.is_system).length;
    const customCount = templates.filter(t => !t.is_system).length;
    const categoryStats = CATEGORIES.slice(1).map(cat => ({
      ...cat,
      count: templates.filter(t => t.category === cat.value).length
    }));
    return { total: templates.length, systemCount, customCount, categoryStats };
  }, [templates]);

  // Fetch all templates (client-side filtering for better UX)
  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await landingPagesApi.getTemplates();
      if (response.success) {
        setTemplates(response.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Load template for editing if ID provided
  useEffect(() => {
    if (id && templates.length > 0) {
      const template = templates.find(t => t.id === id);
      // Allow editing if: custom template OR (system template AND user is admin)
      if (template && (!template.is_system || isAdmin)) {
        setSelectedTemplate(template);
        setFormData({
          name: template.name,
          description: template.description || '',
          category: template.category || 'custom',
          icon: template.icon || null,
          sections: template.sections || []
        });
        setEditOpen(true);
      }
    }
  }, [id, templates, isAdmin]);

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('Template name is required');
      return;
    }

    setSaving(true);
    try {
      const response = await landingPagesApi.createTemplate(formData);
      if (response.success) {
        toast.success('Template created successfully');
        setCreateOpen(false);
        resetForm();
        fetchTemplates();
      } else {
        toast.error(response.detail || 'Failed to create template');
      }
    } catch (error) {
      console.error('Create error:', error);
      toast.error('Failed to create template');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedTemplate) return;

    setSaving(true);
    try {
      const response = await landingPagesApi.updateTemplate(selectedTemplate.id, formData);
      if (response.success) {
        toast.success('Template updated successfully');
        setEditOpen(false);
        setSelectedTemplate(null);
        resetForm();
        fetchTemplates();
        navigate('/page-templates');
      } else {
        toast.error(response.detail || 'Failed to update template');
      }
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Failed to update template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTemplate) return;

    try {
      const response = await landingPagesApi.deleteTemplate(deleteTemplate.id);
      if (response.success) {
        toast.success('Template deleted');
        setDeleteTemplate(null);
        fetchTemplates();
      } else {
        toast.error(response.detail || 'Failed to delete template');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete template');
    }
  };

  const handleDuplicate = async (template) => {
    try {
      const newData = {
        name: `${template.name} (Copy)`,
        description: template.description,
        category: template.category,
        sections: template.sections || []
      };
      const response = await landingPagesApi.createTemplate(newData);
      if (response.success) {
        toast.success('Template duplicated');
        fetchTemplates();
      }
    } catch (error) {
      console.error('Duplicate error:', error);
      toast.error('Failed to duplicate template');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: 'custom',
      icon: null,
      sections: []
    });
  };

  const openEdit = (template) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      category: template.category || 'custom',
      icon: template.icon || null,
      sections: template.sections || []
    });
    setEditOpen(true);
  };

  // Section management
  const addSection = (type) => {
    const sectionInfo = SECTION_TYPES.find(s => s.type === type);
    const newSection = {
      id: `section_${Date.now()}`,
      type,
      order: formData.sections.length,
      visible: true,
      content: {}
    };
    setFormData({
      ...formData,
      sections: [...formData.sections, newSection]
    });
  };

  const removeSection = (sectionId) => {
    setFormData({
      ...formData,
      sections: formData.sections.filter(s => s.id !== sectionId)
    });
  };

  const moveSection = (index, direction) => {
    const newSections = [...formData.sections];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newSections.length) return;
    
    [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
    newSections.forEach((s, i) => s.order = i);
    
    setFormData({ ...formData, sections: newSections });
  };

  const getSectionLabel = (type) => {
    const info = SECTION_TYPES.find(s => s.type === type);
    return info ? info.label : type;
  };

  return (
    <div className="space-y-6" data-testid="page-templates">
      <PageHeader
        title="Page Templates"
        description="Create and manage reusable landing page templates"
        actions={
          <Button onClick={() => { resetForm(); setCreateOpen(true); }} data-testid="create-template-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Template
          </Button>
        }
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-primary/10 to-primary/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Layout className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-xs text-muted-foreground">Total Templates</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <Sparkles className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.systemCount}</p>
                <p className="text-xs text-muted-foreground">System Templates</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/10 rounded-lg">
                <Tag className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.customCount}</p>
                <p className="text-xs text-muted-foreground">Custom Templates</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500/10 rounded-lg">
                <Filter className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{filteredTemplates.length}</p>
                <p className="text-xs text-muted-foreground">Matching Filters</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for System vs Custom */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="all" className="flex items-center gap-2">
            <LayoutGrid className="w-4 h-4" />
            All ({stats.total})
          </TabsTrigger>
          <TabsTrigger value="system" className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            System ({stats.systemCount})
          </TabsTrigger>
          <TabsTrigger value="custom" className="flex items-center gap-2">
            <Tag className="w-4 h-4" />
            Custom ({stats.customCount})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Search and Filters Bar */}
      <div className="flex flex-col md:flex-row gap-4">
        {/* Search Input */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search templates by name, description, or section type..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="template-search-input"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
              onClick={() => setSearchQuery('')}
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
        
        {/* Category Filter */}
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-full md:w-48" data-testid="category-filter">
            <Filter className="w-4 h-4 mr-2 text-muted-foreground" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CATEGORIES.map(cat => {
              const Icon = cat.icon;
              return (
                <SelectItem key={cat.value} value={cat.value}>
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4" />
                    {cat.label}
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        
        {/* Sort Dropdown */}
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-full md:w-44" data-testid="sort-filter">
            <ArrowUpDown className="w-4 h-4 mr-2 text-muted-foreground" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORT_OPTIONS.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {/* View Mode Toggle */}
        <div className="flex border rounded-md">
          {VIEW_MODES.map(mode => {
            const Icon = mode.icon;
            return (
              <Button
                key={mode.value}
                variant={viewMode === mode.value ? "secondary" : "ghost"}
                size="sm"
                className="h-9 px-3"
                onClick={() => setViewMode(mode.value)}
                data-testid={`view-mode-${mode.value}`}
              >
                <Icon className="w-4 h-4" />
              </Button>
            );
          })}
        </div>
      </div>

      {/* Active Filters Display */}
      {(searchQuery || category !== 'all' || activeTab !== 'all') && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">Active filters:</span>
          {searchQuery && (
            <Badge variant="secondary" className="flex items-center gap-1">
              <Search className="w-3 h-3" />
              "{searchQuery}"
              <button onClick={() => setSearchQuery('')} className="ml-1 hover:text-destructive">
                <X className="w-3 h-3" />
              </button>
            </Badge>
          )}
          {category !== 'all' && (
            <Badge variant="secondary" className="flex items-center gap-1">
              <Filter className="w-3 h-3" />
              {CATEGORIES.find(c => c.value === category)?.label}
              <button onClick={() => setCategory('all')} className="ml-1 hover:text-destructive">
                <X className="w-3 h-3" />
              </button>
            </Badge>
          )}
          {activeTab !== 'all' && (
            <Badge variant="secondary" className="flex items-center gap-1">
              {activeTab === 'system' ? <Sparkles className="w-3 h-3" /> : <Tag className="w-3 h-3" />}
              {activeTab === 'system' ? 'System Only' : 'Custom Only'}
              <button onClick={() => setActiveTab('all')} className="ml-1 hover:text-destructive">
                <X className="w-3 h-3" />
              </button>
            </Badge>
          )}
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-xs h-6"
            onClick={() => {
              setSearchQuery('');
              setCategory('all');
              setActiveTab('all');
              setSortBy('name_asc');
            }}
          >
            Clear all
          </Button>
        </div>
      )}

      {/* Templates Grid/List */}
      {loading ? (
        <div className={viewMode === 'grid' 
          ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          : "space-y-3"
        }>
          {[...Array(6)].map((_, i) => (
            <Card key={i}>
              <CardContent className={viewMode === 'grid' ? "pt-6" : "p-4 flex items-center gap-4"}>
                {viewMode === 'grid' ? (
                  <>
                    <Skeleton className="h-32 mb-4" />
                    <Skeleton className="h-6 w-2/3 mb-2" />
                    <Skeleton className="h-4 w-full" />
                  </>
                ) : (
                  <>
                    <Skeleton className="h-12 w-12 rounded" />
                    <div className="flex-1">
                      <Skeleton className="h-5 w-1/3 mb-2" />
                      <Skeleton className="h-4 w-2/3" />
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredTemplates.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Layout className="w-12 h-12 text-muted-foreground/50 mb-4" />
            <p className="text-lg font-medium mb-2">
              {searchQuery || category !== 'all' || activeTab !== 'all' 
                ? 'No templates match your filters' 
                : 'No templates found'}
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              {searchQuery || category !== 'all' || activeTab !== 'all'
                ? 'Try adjusting your search or filter criteria'
                : 'Create your first template to get started'}
            </p>
            {searchQuery || category !== 'all' || activeTab !== 'all' ? (
              <Button 
                variant="outline"
                onClick={() => {
                  setSearchQuery('');
                  setCategory('all');
                  setActiveTab('all');
                }}
              >
                Clear Filters
              </Button>
            ) : (
              <Button onClick={() => { resetForm(); setCreateOpen(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Create Template
              </Button>
            )}
          </CardContent>
        </Card>
      ) : viewMode === 'grid' ? (
        // Grid View
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTemplates.map(template => (
            <Card key={template.id} className="group hover:shadow-md transition-shadow" data-testid={`template-card-${template.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    {/* Template Icon */}
                    <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                      {template.icon ? (
                        <DynamicIcon name={template.icon} className="w-5 h-5" />
                      ) : (
                        <Layout className="w-5 h-5 text-muted-foreground" />
                      )}
                    </div>
                    <div>
                      <CardTitle className="text-lg">{template.name}</CardTitle>
                      <div className="flex gap-2 mt-1 flex-wrap">
                        <Badge variant={template.is_system ? "secondary" : "outline"}>
                          {template.is_system ? 'System' : template.category}
                        </Badge>
                        {template.sections && (
                          <Badge variant="outline" className="text-xs">
                            {template.sections.length} sections
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {template.is_system && !isAdmin ? (
                        <>
                          <DropdownMenuItem onClick={() => handleDuplicate(template)} className="text-primary font-medium">
                            <Copy className="w-4 h-4 mr-2" />
                            Duplicate to Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <div className="px-2 py-1.5 text-xs text-muted-foreground">
                            System templates are read-only.
                            <br />
                            Duplicate to create an editable copy.
                          </div>
                        </>
                      ) : template.is_system && isAdmin ? (
                        <>
                          <DropdownMenuItem onClick={() => openEdit(template)}>
                            <Pencil className="w-4 h-4 mr-2" />
                            Edit System Template
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDuplicate(template)}>
                            <Copy className="w-4 h-4 mr-2" />
                            Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <div className="px-2 py-1.5 text-xs text-muted-foreground flex items-center gap-1">
                            <ShieldCheck className="w-3 h-3" />
                            Admin access enabled
                          </div>
                        </>
                      ) : (
                        <>
                          <DropdownMenuItem onClick={() => openEdit(template)}>
                            <Pencil className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDuplicate(template)}>
                            <Copy className="w-4 h-4 mr-2" />
                            Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => setDeleteTemplate(template)}
                            className="text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {template.description || 'No description'}
                </p>
                {/* Template Preview - Icon fallback for broken/missing images */}
                <div className="mt-3 rounded-md w-full h-32 bg-muted/30 border flex items-center justify-center overflow-hidden">
                  {template.preview_image_url ? (
                    <img 
                      src={template.preview_image_url} 
                      alt={template.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // Hide broken image, show icon fallback
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div 
                    className={`${template.preview_image_url ? 'hidden' : 'flex'} flex-col items-center justify-center gap-2 text-muted-foreground/50 w-full h-full`}
                    style={{ display: template.preview_image_url ? 'none' : 'flex' }}
                  >
                    <div className="w-12 h-12">
                      {getTemplateIcon(template.icon)}
                    </div>
                    <span className="text-xs">{template.name}</span>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="pt-0">
                <div className="flex gap-2 w-full">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => navigate(`/pages/new?template=${template.id}`)}
                    data-testid={`use-template-${template.id}`}
                  >
                    Use Template
                  </Button>
                </div>
              </CardFooter>
            </Card>
          ))}
        </div>
      ) : (
        // List View
        <div className="space-y-2">
          {filteredTemplates.map(template => (
            <Card key={template.id} className="group hover:shadow-sm transition-shadow" data-testid={`template-row-${template.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  {/* Icon/Preview */}
                  <div className="shrink-0">
                    {template.preview_image_url ? (
                      <img 
                        src={template.preview_image_url} 
                        alt={template.name}
                        className="w-16 h-16 rounded-md object-cover"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-muted rounded-md flex items-center justify-center">
                        {template.icon ? (
                          <DynamicIcon name={template.icon} className="w-8 h-8" />
                        ) : (
                          <Layout className="w-8 h-8 text-muted-foreground" />
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold truncate">{template.name}</h3>
                      <Badge variant={template.is_system ? "secondary" : "outline"} className="shrink-0">
                        {template.is_system ? 'System' : template.category}
                      </Badge>
                      <Badge variant="outline" className="text-xs shrink-0">
                        {template.sections?.length || 0} sections
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground truncate">
                      {template.description || 'No description'}
                    </p>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2 shrink-0">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => navigate(`/pages/new?template=${template.id}`)}
                    >
                      Use Template
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {template.is_system && !isAdmin ? (
                          <>
                            <DropdownMenuItem onClick={() => handleDuplicate(template)} className="text-primary font-medium">
                              <Copy className="w-4 h-4 mr-2" />
                              Duplicate to Edit
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <div className="px-2 py-1.5 text-xs text-muted-foreground">
                              System templates are read-only.
                              <br />
                              Duplicate to create an editable copy.
                            </div>
                          </>
                        ) : template.is_system && isAdmin ? (
                          <>
                            <DropdownMenuItem onClick={() => openEdit(template)}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Edit System Template
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDuplicate(template)}>
                              <Copy className="w-4 h-4 mr-2" />
                              Duplicate
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <div className="px-2 py-1.5 text-xs text-muted-foreground flex items-center gap-1">
                              <ShieldCheck className="w-3 h-3" />
                              Admin access enabled
                            </div>
                          </>
                        ) : (
                          <>
                            <DropdownMenuItem onClick={() => openEdit(template)}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDuplicate(template)}>
                              <Copy className="w-4 h-4 mr-2" />
                              Duplicate
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => setDeleteTemplate(template)}
                              className="text-destructive"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Template Dialog */}
      <Dialog open={createOpen || editOpen} onOpenChange={(open) => {
        if (!open) {
          setCreateOpen(false);
          setEditOpen(false);
          setSelectedTemplate(null);
          resetForm();
          if (id) navigate('/page-templates');
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {editOpen ? 'Edit Template' : 'Create New Template'}
            </DialogTitle>
            <DialogDescription>
              Define the structure and sections for your page template
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Template Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Property Listing"
                  data-testid="template-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(v) => setFormData({ ...formData, category: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.filter(c => c.value !== 'all').map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <IconPicker
                  label="Template Icon"
                  value={formData.icon}
                  onChange={(icon) => setFormData({ ...formData, icon })}
                  placeholder="Select an icon"
                  testId="template-icon-picker"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of this template..."
                  rows={2}
                  data-testid="template-description-input"
                />
              </div>
            </div>

            {/* Sections Builder */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Template Sections</Label>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Plus className="w-4 h-4 mr-1" />
                      Add Section
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    {SECTION_TYPES.map(section => (
                      <DropdownMenuItem 
                        key={section.type}
                        onClick={() => addSection(section.type)}
                      >
                        <div>
                          <div className="font-medium">{section.label}</div>
                          <div className="text-xs text-muted-foreground">{section.description}</div>
                        </div>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {formData.sections.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="flex flex-col items-center justify-center py-8">
                    <FileText className="w-10 h-10 text-muted-foreground/50 mb-2" />
                    <p className="text-sm text-muted-foreground">No sections added</p>
                    <p className="text-xs text-muted-foreground">Click "Add Section" to start building</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2">
                  {formData.sections.map((section, index) => (
                    <div 
                      key={section.id}
                      className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg border"
                    >
                      <GripVertical className="w-4 h-4 text-muted-foreground cursor-move" />
                      <div className="flex-1">
                        <span className="font-medium">{getSectionLabel(section.type)}</span>
                        <span className="text-xs text-muted-foreground ml-2">({section.type})</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => moveSection(index, 'up')}
                          disabled={index === 0}
                        >
                          <ChevronUp className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => moveSection(index, 'down')}
                          disabled={index === formData.sections.length - 1}
                        >
                          <ChevronDown className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                          onClick={() => removeSection(section.id)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="border-t pt-4">
            <Button 
              variant="outline" 
              onClick={() => {
                setCreateOpen(false);
                setEditOpen(false);
                setSelectedTemplate(null);
                resetForm();
                if (id) navigate('/page-templates');
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={editOpen ? handleUpdate : handleCreate}
              disabled={saving || !formData.name.trim()}
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editOpen ? 'Save Changes' : 'Create Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTemplate} onOpenChange={(open) => !open && setDeleteTemplate(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteTemplate?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
